import { FileUploader, FileUploaderAttachedFile, ScrollContainer } from "@skbkontur/react-ui";
import { Button, Loader, Modal } from "react-ui";
import React, { useEffect, useReducer, useState } from "react";

import { useExternDriveClient } from "../../../Services/Contexts/ExternDriveClientContext";
import { UploadFileStatuses } from "../../../../Engine/Core/ModalManager/Modals/Utils/Types";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { BulkFiles } from "../../../../Engine/Externals/Attachments/IAttachmentsManager";
import { getCandyHub } from "../../../../Forms/Sentry/InitializeSentry";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { UPLOAD_FUF_ATTACHMENTS } from "../../../metricsAction";

import { UploadHintText, ModalBodyList } from "./FufWithAttachmentsUploadModal.styles";
import { SCROLL_CONTAINER_HEIGHT, UnitList } from "./List/UnitList";
import { AttachmentNames, UnitItem } from "./FufAndAttachmentsUploadModalTypes";
import { addItems, checkUniqueGfv, fufAndAttachmentsReducer } from "./FufAndAttachmentsReducer";
import { FufAndAttachmentsUploadProvider } from "./FufAndAttachmentsUploadContext";
import { createBulkAttachmentsFiles, createUploadTask, GetProperties } from "./actions";
import { FufAndAttachmentsSuccessMiniModal } from "./FufAndAttachmentsSuccessMiniModal";

export interface FilePropertiesOptions {
    declarationGfv: string;
    isFormInFormNds: boolean;
}

export interface FufAndAttachmentsUploadModalProps {
    onClose: () => void;
    getFileProperties: GetProperties;
    currentGfv: string;
    isFormInFormNds: boolean;
    attachmentNames: AttachmentNames;
    setFuf: (driveUri: string) => Promise<void>;
    reloadForm: (useServerSideFLangNormalization?: boolean) => Promise<void>;
    useServerSideFLangNormalization?: boolean;
    getNextAttachmentBindingPath: (bindingPath: AbsoluteModelPath) => AbsoluteResolvedModelPath;
    onLoadAttachmentFile: (
        attachmentUnit: UnitItem,
        gfv: string,
        bindingPath: AbsoluteResolvedModelPath
    ) => Promise<void>;
    addBulkAttachments: (bulkFiles: BulkFiles[]) => Promise<void>;
}

export const FufAndAttachmentsUploadModal = (props: FufAndAttachmentsUploadModalProps) => {
    const scrollRef = React.useRef<ScrollContainer | null>(null);
    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const { upload } = useExternDriveClient();
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingError, setUploadingError] = useState<boolean>(false);
    const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
    const track = useMetrics();

    const [state, dispatch] = useReducer(fufAndAttachmentsReducer, []);

    useEffect(() => {
        dispatch(checkUniqueGfv({ gfv: props.currentGfv }));
    }, [state.length]);

    const onAttach = async (files: FileUploaderAttachedFile[]) => {
        const tasks = [];
        const attachedFiles: UnitItem[] = [];
        for (const file of files) {
            attachedFiles.push({
                file: {
                    name: file.originalFile.name,
                    size: file.originalFile.size,
                },
                title: file.originalFile.name,
                id: file.id,
                status: UploadFileStatuses.new,
                gfv: "none",
            });

            tasks.push(async () =>
                createUploadTask({
                    dispatch: dispatch,
                    upload: upload,
                    file: file,
                    currentGfv: props.currentGfv,
                    isFormInFormNds: props.isFormInFormNds,
                    getFileProperties: props.getFileProperties,
                })
            );
        }
        dispatch(addItems(attachedFiles));

        await Promise.all(tasks.map(task => task()));
    };

    const save = async () => {
        setLoading(true);
        setUploadingError(false);

        const bulkAttachmentsFiles: BulkFiles[] = createBulkAttachmentsFiles({
            attachmentNames: props.attachmentNames,
            currentGfv: props.currentGfv,
            state: state,
        });

        const declaration = state.find(
            item => item.gfv === props.currentGfv && item.status === UploadFileStatuses.success
        );

        try {
            await props.addBulkAttachments(bulkAttachmentsFiles);

            if (declaration?.file?.contentUri) {
                await props.setFuf(declaration.file.contentUri);
            }

            await props.reloadForm(props.useServerSideFLangNormalization);
            setLoading(false);

            track(UPLOAD_FUF_ATTACHMENTS);
            setUploadSuccess(true);
        } catch (error) {
            getCandyHub().captureException(error);
            setUploadingError(true);
        } finally {
            setLoading(false);
        }
    };

    const canUpload =
        state.some(item => item.status === UploadFileStatuses.success) &&
        !state.some(item => item.status === UploadFileStatuses.progress);
    const hasErrors = state.some(item => item.status === UploadFileStatuses.error);

    if (uploadSuccess) {
        return <FufAndAttachmentsSuccessMiniModal hasAttachmentErrors={hasErrors} onClose={props.onClose} />;
    }

    return (
        <FufAndAttachmentsUploadProvider dispatch={dispatch}>
            <Modal ignoreBackgroundClick width={660} onClose={props.onClose}>
                <Modal.Header>Загрузка файла отчёта с приложениями</Modal.Header>
                <Modal.Body>
                    <Loader type="big" active={loading} caption="Загрузка">
                        <FileUploader onAttach={onAttach} multiple hideFiles width="100%" />
                        {state.length === 0 && (
                            <UploadHintText>Можно выбрать один или несколько файлов любого из разделов</UploadHintText>
                        )}
                        {uploadingError && (
                            <UploadHintText isError>Произошла ошибка, попробуйте ещё раз</UploadHintText>
                        )}
                        <ModalBodyList isScrolled={isScrolled}>
                            <ScrollContainer maxHeight={SCROLL_CONTAINER_HEIGHT} ref={scrollRef}>
                                <UnitList
                                    setIsScrolled={setIsScrolled}
                                    filesForUpload={state}
                                    attachmentNames={props.attachmentNames}
                                    scrollRef={scrollRef}
                                />
                            </ScrollContainer>
                        </ModalBodyList>
                    </Loader>
                </Modal.Body>
                <Modal.Footer gap={8}>
                    <Button
                        data-tid={"upload"}
                        disabled={loading || !canUpload}
                        use="primary"
                        size="medium"
                        onClick={async () => {
                            await save();
                        }}>
                        {canUpload && hasErrors ? "Загрузить файлы без ошибок" : "Загрузить"}
                    </Button>
                    <Button
                        size="medium"
                        onClick={() => {
                            props.onClose();
                        }}>
                        Отмена
                    </Button>
                </Modal.Footer>
            </Modal>
        </FufAndAttachmentsUploadProvider>
    );
};
